import type { MouseEvent, RefObject, TouchEvent } from 'react';
import { useEffect, useRef } from 'react';
import type { Nullable } from 'corgi-types';
import debounce from 'lodash-es/debounce';

import type { CarouselProps, HandlerRef } from '..';
import type { SwipeDirection } from '../constants';
import { SCROLL_LISTENER_DEBOUNCE } from '../constants';
import {
  getActiveSlide,
  getScrollLeftFromIndex,
  getSwipeDistance,
  getSwipePosition,
  isScrollable,
  isSwipeable,
} from '../utils/scroll';

export type SliderEvent = MouseEvent<HTMLDivElement> | TouchEvent<HTMLDivElement> | Event;

interface CarouselInteractionProps extends CarouselProps {
  carouselRef: RefObject<Nullable<HTMLDivElement>>;
  handlerRef?: HandlerRef;
  setScroll?: (arg: number) => void;
}

export const useCarousel = ({
  handlerRef,
  carouselRef,
  scrollSlidesAmount,
  afterSlide,
  setScroll,
}: CarouselInteractionProps) => {
  const isSwiping = useRef(false);

  const handleAfterScroll = (newScrollLeft: number) => {
    if (setScroll) {
      setScroll(newScrollLeft);
    }
    // callback after sliding
    if (afterSlide) {
      afterSlide(getActiveSlide(carouselRef, newScrollLeft));
    }
    isSwiping.current = false;
  };

  const swipe = (swipeDirection: SwipeDirection) => {
    const swipeable = isSwipeable(carouselRef, swipeDirection);
    if (carouselRef.current && !isSwiping.current && swipeable) {
      isSwiping.current = true;
      const swipeDistance = getSwipeDistance(carouselRef, scrollSlidesAmount);
      const newScrollLeft = getSwipePosition(carouselRef, swipeDistance, swipeDirection);
      carouselRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth',
      });
    }
  };

  const scrollToIndex = (index: number) => {
    if (carouselRef.current && !isSwiping.current) {
      const scrollLeft = getScrollLeftFromIndex(carouselRef, index);
      carouselRef.current.scrollTo({
        left: scrollLeft,
        behavior: 'smooth',
      });
    }
  };

  // Set carousel swipe handlers
  useEffect(() => {
    if (handlerRef && carouselRef.current) {
      handlerRef.current = {
        swipe: (dir: SwipeDirection) => swipe(dir),
        scrollToIndex: (index: number) => scrollToIndex(index),
        isScrollable: (scroll: number, direction: SwipeDirection) =>
          isScrollable(carouselRef, scroll, direction),
      };
    }
  }, [swipe, scrollToIndex]);

  const handleScroll = debounce(() => {
    handleAfterScroll(Math.ceil(carouselRef.current?.scrollLeft || 0));
  }, SCROLL_LISTENER_DEBOUNCE);

  return { carouselRef, handleScroll };
};
